import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Info } from 'lucide-react';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  slippageTolerance: number;
  onSlippageChange: (value: number) => void;
  transactionTimeout: number;
  onTimeoutChange: (value: number) => void;
}

export default function SettingsModal({ 
  open, 
  onOpenChange, 
  slippageTolerance, 
  onSlippageChange,
  transactionTimeout,
  onTimeoutChange
}: SettingsModalProps) {
  const [customSlippage, setCustomSlippage] = useState(slippageTolerance.toString());
  const [autoSlippage, setAutoSlippage] = useState(false);
  const [customTimeout, setCustomTimeout] = useState(transactionTimeout.toString());
  const [autoTimeout, setAutoTimeout] = useState(false);

  useEffect(() => {
    setCustomSlippage(slippageTolerance.toString());
  }, [slippageTolerance]);

  useEffect(() => {
    setCustomTimeout(transactionTimeout.toString());
  }, [transactionTimeout]);

  const handleSlippageChange = (value: string) => {
    setCustomSlippage(value);
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0 && numValue <= 50) {
      onSlippageChange(numValue);
    }
  };

  const handlePresetSlippage = (value: number) => {
    setCustomSlippage(value.toString());
    onSlippageChange(value);
    setAutoSlippage(false);
  };

  const handleTimeoutChange = (value: string) => {
    setCustomTimeout(value);
    const numValue = parseInt(value);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 60) {
      onTimeoutChange(numValue);
    }
  };

  const handlePresetTimeout = (value: number) => {
    setCustomTimeout(value.toString());
    onTimeoutChange(value);
    setAutoTimeout(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Swap Settings</DialogTitle>
          <DialogDescription>
            Configure your swap preferences and transaction parameters
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Slippage Tolerance Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Slippage Tolerance</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum price difference you're willing to accept
                </p>
              </div>
              <Switch
                checked={autoSlippage}
                onCheckedChange={setAutoSlippage}
              />
            </div>

            {!autoSlippage && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePresetSlippage(0.1)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customSlippage === '0.1'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    0.1%
                  </button>
                  <button
                    onClick={() => handlePresetSlippage(0.5)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customSlippage === '0.5'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    0.5%
                  </button>
                  <button
                    onClick={() => handlePresetSlippage(1.0)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customSlippage === '1' || customSlippage === '1.0'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    1.0%
                  </button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-slippage" className="text-sm">
                    Custom Slippage (%)
                  </Label>
                  <Input
                    id="custom-slippage"
                    type="number"
                    min="0"
                    max="50"
                    step="0.1"
                    value={customSlippage}
                    onChange={(e) => handleSlippageChange(e.target.value)}
                    placeholder="0.5"
                  />
                </div>

                {parseFloat(customSlippage) > 5 && (
                  <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3 flex items-start gap-2">
                    <Info className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-yellow-500">
                      High slippage tolerance may result in unfavorable rates
                    </p>
                  </div>
                )}
              </div>
            )}

            {autoSlippage && (
              <div className="bg-accent/30 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  Auto slippage is enabled. The system will automatically calculate optimal slippage based on market conditions.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Transaction Timeout Section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-base font-semibold">Transaction Timeout</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Maximum time to wait for transaction confirmation
                </p>
              </div>
              <Switch
                checked={autoTimeout}
                onCheckedChange={setAutoTimeout}
              />
            </div>

            {!autoTimeout && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => handlePresetTimeout(10)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customTimeout === '10'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    10 min
                  </button>
                  <button
                    onClick={() => handlePresetTimeout(20)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customTimeout === '20'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    20 min
                  </button>
                  <button
                    onClick={() => handlePresetTimeout(30)}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors ${
                      customTimeout === '30'
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    30 min
                  </button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom-timeout" className="text-sm">
                    Custom Timeout (minutes)
                  </Label>
                  <Input
                    id="custom-timeout"
                    type="number"
                    min="1"
                    max="60"
                    value={customTimeout}
                    onChange={(e) => handleTimeoutChange(e.target.value)}
                    placeholder="20"
                  />
                </div>
              </div>
            )}

            {autoTimeout && (
              <div className="bg-accent/30 rounded-lg p-3">
                <p className="text-sm text-muted-foreground">
                  Auto timeout is enabled. The system will use network-appropriate timeout values.
                </p>
              </div>
            )}
          </div>

          <Separator />

          {/* Info Section */}
          <div className="bg-primary/10 rounded-lg p-4 flex items-start gap-3">
            <Info className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Functional Settings</p>
              <p className="text-xs text-muted-foreground">
                These settings affect real swap execution. Slippage tolerance and transaction timeout are applied to all swap operations.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
